import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "window", "cursor", "draggedPhotos", "photoDropZone", 
    "classificationHeader", "attributesBody", "attributesContainer", "attributesHeader", "rightColumn"
  ]
  
  static values = { 
    demoData: Array 
  }

  connect() {
    this.currentDataIndex = 0
    this.isAnimating = false
    
    // Use the demo data passed from the component, or fall back to empty array
    this.demoData = this.demoDataValue || []
    
    // Only start animation if we have data
    if (this.demoData.length > 0) {
      // Start the animation cycle
      setTimeout(() => this.startAnimationCycle(), 1000)
    }
  }

  disconnect() {
    if (this.animationTimeout) {
      clearTimeout(this.animationTimeout)
    }
  }

  startAnimationCycle() {
    if (this.isAnimating) return
    
    this.isAnimating = true
    this.currentData = this.demoData[this.currentDataIndex]
    
    // Clear previous content
    this.clearContent()
    
    // Start the sequence
    this.animateMouseEntering()
  }

  clearContent() {
    // Clear photos
    this.photoDropZoneTarget.innerHTML = ''
    
    // Clear classification and reset classes
    this.classificationHeaderTarget.innerHTML = ''
    this.classificationHeaderTarget.classList.remove('visible', 'fade-out')
    
    // Clear attributes
    this.attributesBodyTarget.innerHTML = ''
    
    // Hide table headers and reset classes
    this.attributesHeaderTarget.classList.remove('visible', 'fade-out')
    
    // Hide elements
    this.cursorTarget.classList.remove('visible')
    this.draggedPhotosTarget.innerHTML = ''
  }

  animateMouseEntering() {
    // Create dragged photos
    this.createDraggedPhotos()
    
    // Show cursor
    this.cursorTarget.classList.add('visible')
    
    // Small delay to ensure CSS transitions are applied
    setTimeout(() => {
      // Get element bounds for relative positioning
      const elementRect = this.element.getBoundingClientRect()
      const centerX = elementRect.width * 0.5
      const centerY = elementRect.height * 0.5
      
      // Animate cursor from far off-screen to window edge, then to center
      this.animateCursorPath([
        { x: -200, y: -150 },
        { x: centerX, y: centerY }
      ], 0, () => {
        // Give window time to grow before dropping photos
        setTimeout(() => {
          this.animatePhotoDrop()
        }, 600)
      }, 1) // Trigger window grow at index 1 (when entering window)
    }, 50)
  }

  createDraggedPhotos() {
    const photos = this.currentData.photos
    this.draggedPhotosTarget.innerHTML = ''
    
    photos.forEach((photo, index) => {
      const photoEl = document.createElement('div')
      photoEl.className = 'dragged-photo'
      photoEl.style.backgroundImage = `url(${photo.url})`
      photoEl.style.backgroundSize = 'cover'
      photoEl.style.backgroundPosition = 'center'
      
      // Set initial position far off-screen centered around cursor
      const offsetX = (index - 1) * 25 - 72  // 72px = half of 9rem (144px) to center
      const offsetY = index * 20 - 72        // 72px = half of 9rem (144px) to center
      photoEl.style.left = `${-200 + offsetX}px`
      photoEl.style.top = `${-150 + offsetY}px`
      
      this.draggedPhotosTarget.appendChild(photoEl)
    })
  }

  animateWindowGrow() {
    const window = this.element.querySelector('.demo-window')
    window.classList.add('drop-ready')
  }

  animatePhotoDrop() {
    const window = this.element.querySelector('.demo-window')
    
    // Hide dragged photos
    this.draggedPhotosTarget.innerHTML = ''
    
    // Bounce window back to normal
    window.classList.remove('drop-ready')
    window.classList.add('bounce')
    
    setTimeout(() => {
      window.classList.remove('bounce')
      this.animatePhotosAppearing()
    }, 600)
  }

  animatePhotosAppearing() {
    const photos = this.currentData.photos
    
    photos.forEach((photo, index) => {
      setTimeout(() => {
        this.createPhotoElement(photo, index)
      }, index * 500)
    })
    
    // After all photos are loaded, show classification
    setTimeout(() => {
      this.animateClassificationAppearing()
    }, photos.length * 500 + 300)
  }

  createPhotoElement(photo, index) {
    const photoEl = document.createElement('div')
    photoEl.className = 'photo-item bounce'
    
    photoEl.innerHTML = `
      <div class="photo-image" style="background-image: url(${photo.url}); background-size: cover; background-position: center;"></div>
      <div class="photo-label">${photo.label}</div>
      ${photo.representative ? '<svg class="photo-star" fill="currentColor" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"></path></svg>' : ''}
    `
    
    this.photoDropZoneTarget.appendChild(photoEl)
  }

  animateClassificationAppearing() {
    const headerEl = document.createElement('h2')
    headerEl.className = 'classification-title'
    headerEl.textContent = this.currentData.classification
    
    this.classificationHeaderTarget.appendChild(headerEl)
    this.classificationHeaderTarget.classList.add('visible')
    
    // Show table headers after classification appears
    setTimeout(() => {
      this.attributesHeaderTarget.classList.add('visible')
      
      setTimeout(() => {
        this.animateAttributesAppearing()
      }, 400)
    }, 600)
  }

  animateAttributesAppearing() {
    const attributes = this.currentData.attributes
    
    attributes.forEach((attr, index) => {
      setTimeout(() => {
        this.createAttributeRow(attr, index)
      }, index * 200)
    })
    
    // After all attributes are shown, animate mouse for scrolling
    setTimeout(() => {
      this.animateMouseScrolling()
    }, attributes.length * 200 + 1000)
  }

  createAttributeRow(attr, index) {
    const row = document.createElement('tr')
    
    // Determine confidence levels for background and badge
    const confidenceLevel = attr.confidence >= 8 ? 'high' : 
                           attr.confidence >= 5 ? 'medium' : 'low'
    
    row.className = `attribute-row visible confidence-${confidenceLevel}`
    
    row.innerHTML = `
      <td class="attribute-label">${attr.label}</td>
      <td class="attribute-value">${attr.value}</td>
      <td><span class="confidence-badge ${confidenceLevel}">${attr.confidence}</span></td>
      <td class="attribute-reasoning">${attr.reasoning}</td>
    `
    
    this.attributesBodyTarget.appendChild(row)
  }

  animateMouseScrolling() {
    // Move cursor to table area
    const tableRect = this.attributesContainerTarget.getBoundingClientRect()
    const elementRect = this.element.getBoundingClientRect()
    
    const targetX = tableRect.left - elementRect.left + tableRect.width / 2
    const targetY = tableRect.top - elementRect.top + tableRect.height / 2
    
    this.animateCursorPath([
      { x: targetX, y: targetY - 30 },
      { x: targetX, y: targetY + 40 }
    ], 0, () => {
      // Slow scroll the right column to show all attributes
      const rightColumn = this.rightColumnTarget
      const scrollHeight = rightColumn.scrollHeight
      const clientHeight = rightColumn.clientHeight
      
      if (scrollHeight > clientHeight) {
        // Custom slow scroll animation
        this.slowScrollTo(rightColumn, scrollHeight - clientHeight, 4000)
      }
      
      setTimeout(() => {
        this.animateMouseExiting()
      }, 4000) // Extended to account for slow scroll
    })
  }

  animateMouseExiting() {
    // Get element bounds for relative positioning
    const elementRect = this.element.getBoundingClientRect()
    
    // Move cursor far off screen smoothly
    this.animateCursorPath([
      { x: elementRect.width + 200, y: -150 }
    ], 0, () => {
      this.cursorTarget.classList.remove('visible')
      this.prepareNextCycle()
    })
  }

  animateCursorPath(points, currentIndex, callback, triggerIndex = null) {
    if (currentIndex >= points.length) {
      if (callback) callback()
      return
    }
    
    const point = points[currentIndex]
    
    // Set cursor position with smooth CSS transition
    this.cursorTarget.style.left = `${point.x}px`
    this.cursorTarget.style.top = `${point.y}px`
    
    // Update dragged photos position with staggered offsets centered around cursor
    const draggedPhotos = this.draggedPhotosTarget.children
    for (let i = 0; i < draggedPhotos.length; i++) {
      const photo = draggedPhotos[i]
      // Center photos around cursor with staggered positioning
      const offsetX = (i - 1) * 25 - 72  // 72px = half of 9rem (144px) to center
      const offsetY = i * 20 - 72        // 72px = half of 9rem (144px) to center
      photo.style.left = `${point.x + offsetX}px`
      photo.style.top = `${point.y + offsetY}px`
    }
    
    // Trigger window grow if we've reached the trigger point
    if (triggerIndex !== null && currentIndex === triggerIndex) {
      this.animateWindowGrow()
    }
    
    // Wait for CSS transition to complete before next point
    setTimeout(() => {
      this.animateCursorPath(points, currentIndex + 1, callback, triggerIndex)
    }, 1200)
  }

  animateSlowCursorPath(points, currentIndex, callback) {
    if (currentIndex >= points.length) {
      // Reset transition speed back to normal
      this.cursorTarget.style.transition = 'all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
      if (callback) callback()
      return
    }
    
    const point = points[currentIndex]
    
    // Set much slower CSS transition for scrolling demonstration
    this.cursorTarget.style.transition = 'all 3s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
    
    // Set cursor position with slower CSS transition for scrolling
    this.cursorTarget.style.left = `${point.x}px`
    this.cursorTarget.style.top = `${point.y}px`
    
    // Wait for the slow CSS transition to complete
    setTimeout(() => {
      this.animateSlowCursorPath(points, currentIndex + 1, callback)
    }, 3200) // Wait for 3s transition + small buffer
  }

  slowScrollTo(element, targetScrollTop, duration) {
    const startScrollTop = element.scrollTop
    const distance = targetScrollTop - startScrollTop
    const startTime = performance.now()
    
    const easeInOutCubic = (t) => {
      return t < 0.5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1
    }
    
    const animateScroll = (currentTime) => {
      const elapsed = currentTime - startTime
      const progress = Math.min(elapsed / duration, 1)
      const easedProgress = easeInOutCubic(progress)
      
      element.scrollTop = startScrollTop + distance * easedProgress
      
      if (progress < 1) {
        requestAnimationFrame(animateScroll)
      }
    }
    
    requestAnimationFrame(animateScroll)
  }

  prepareNextCycle() {
    // Start the fade-out sequence
    this.fadeOutCurrentContent(() => {
      // Reset scroll position
      this.rightColumnTarget.scrollTo({
        top: 0,
        behavior: 'smooth'
      })
      
      // Move to next data set
      this.currentDataIndex = (this.currentDataIndex + 1) % this.demoData.length
      this.isAnimating = false
      
      // Start next cycle after delay
      this.animationTimeout = setTimeout(() => {
        this.startAnimationCycle()
      }, 1000)
    })
  }

  fadeOutCurrentContent(callback) {
    // Fade out in coordinated sequence
    const attributeRows = this.attributesBodyTarget.querySelectorAll('.attribute-row')
    const photos = this.photoDropZoneTarget.querySelectorAll('.photo-item')
    
    // 1. Fade out attribute rows and photos simultaneously (both staggered)
    attributeRows.forEach((row, index) => {
      setTimeout(() => {
        row.classList.remove('visible')
        row.classList.add('fade-out')
      }, index * 100)
    })
    
    // Fade out photos in reverse order at the same time
    Array.from(photos).reverse().forEach((photo, index) => {
      setTimeout(() => {
        photo.classList.add('fade-out')
      }, index * 150)
    })
    
    // 2. After main content fades out, fade out table headers
    const mainContentFadeTime = Math.max(attributeRows.length * 100, photos.length * 150)
    setTimeout(() => {
      this.attributesHeaderTarget.classList.remove('visible')
      this.attributesHeaderTarget.classList.add('fade-out')
    }, mainContentFadeTime + 200)
    
    // 3. Finally fade out classification header
    setTimeout(() => {
      this.classificationHeaderTarget.classList.remove('visible')
      this.classificationHeaderTarget.classList.add('fade-out')
    }, mainContentFadeTime + 600)
    
    // 4. Call callback when all fade-outs complete
    const totalFadeTime = mainContentFadeTime + 1000
    setTimeout(callback, totalFadeTime)
  }
}
